import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Briefcase, Search, MapPin, AlertTriangle } from 'lucide-react';

interface BrokerClient {
  id: string;
  name?: string;
  trading_name?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  city?: string;
  province?: string;
  site_count?: number;
  active?: boolean;
}

export const BrokerClients: React.FC = () => {
  const [clients, setClients] = useState<BrokerClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await api.get<BrokerClient[]>('/clients');
        if (!cancelled) setClients(r.data || []);
      } catch {
        if (!cancelled) setError('We could not load your clients. Please try again shortly.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = clients.filter((c) => {
    if (!q.trim()) return true;
    const hay = `${c.name || ''} ${c.trading_name || ''} ${c.city || ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div className="space-y-4 animate-fade-in" data-testid="broker-clients-page">
      <div className="flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Clients</h2>
          <Badge variant="muted" data-testid="broker-clients-count">
            {loading ? 'Loading…' : `${clients.length} client${clients.length === 1 ? '' : 's'}`}
          </Badge>
        </div>
        {clients.length > 0 && (
          <div className="relative w-full sm:w-72">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients…"
              className="pl-9"
              data-testid="broker-clients-search"
            />
          </div>
        )}
      </div>

      {loading && (
        <div className="grid gap-3" data-testid="broker-clients-loading">
          {[0, 1, 2].map((i) => (
            <Card key={i}><CardContent className="py-6">
              <div className="h-4 w-48 bg-raptor-neutral/60 rounded animate-pulse mb-3" />
              <div className="h-3 w-72 bg-raptor-neutral/40 rounded animate-pulse" />
            </CardContent></Card>
          ))}
        </div>
      )}

      {!loading && error && (
        <Card><CardContent className="py-12 flex flex-col items-center text-center" data-testid="broker-clients-error">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertTriangle size={22} className="text-red-500" />
          </div>
          <div className="font-body text-raptor-navy/70 max-w-md">{error}</div>
        </CardContent></Card>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Card><CardContent className="py-12 flex flex-col items-center text-center" data-testid="broker-clients-empty">
          <div className="w-14 h-14 rounded-full bg-raptor-neutral/60 flex items-center justify-center mb-4">
            <Briefcase size={22} className="text-raptor-navy/40" />
          </div>
          <div className="font-body text-raptor-navy/70 max-w-md">
            {clients.length === 0
              ? 'No clients yet. Once your insurer has loaded clients under your brokerage, they will appear here.'
              : 'No clients match your search.'}
          </div>
        </CardContent></Card>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="grid gap-3" data-testid="broker-clients-list">
          {filtered.map((c) => (
            <Card key={c.id} className="transition-shadow hover:shadow-md" data-testid={`broker-client-row-${c.id}`}>
              <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-raptor-navy truncate">{c.trading_name || c.name || c.id}</span>
                    {c.active === false && <Badge variant="muted">Inactive</Badge>}
                  </div>
                  {c.trading_name && c.name && c.trading_name !== c.name && (
                    <div className="text-xs text-raptor-navy/50 truncate">{c.name}</div>
                  )}
                  <div className="text-xs text-raptor-navy/60 mt-1 flex items-center gap-3 flex-wrap">
                    {c.primary_contact_name && <span>{c.primary_contact_name}</span>}
                    {(c.city || c.province) && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} /> {[c.city, c.province].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-tabular font-bold text-raptor-navy" data-testid={`broker-client-sites-${c.id}`}>
                    {c.site_count ?? 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-raptor-navy/50">
                    site{(c.site_count ?? 0) === 1 ? '' : 's'}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
